import React, { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ShoppingBag, Menu, X, Search } from "lucide-react";
import { useCart } from "../context/CartContext";

const navLinks = [
  { to: "/shop", label: "All" },
  { to: "/shop/women", label: "Women" },
  { to: "/shop/men", label: "Men" },
  { to: "/shop/unisex", label: "Unisex" },
];

const Navbar = () => {
  const { count, setOpen } = useCart();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-40 w-full transition-all duration-500 ${
        scrolled
          ? "bg-cream/85 backdrop-blur-xl border-b border-gold-soft/60"
          : "bg-transparent"
      }`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Mobile menu trigger */}
          <button
            data-testid="mobile-menu-trigger"
            onClick={() => setMobileOpen((v) => !v)}
            className="md:hidden text-ink p-2 -ml-2"
            aria-label="Menu"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>

          {/* Desktop nav left */}
          <nav className="hidden md:flex items-center gap-10">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/shop"}
                data-testid={`nav-${l.label.toLowerCase()}-link`}
                className={({ isActive }) =>
                  `link-underline text-[11px] uppercase tracking-[0.28em] font-medium transition-colors ${
                    isActive ? "text-gold" : "text-ink hover:text-gold"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Logo */}
          <Link
            to="/"
            data-testid="logo-link"
            className="absolute left-1/2 -translate-x-1/2 select-none"
          >
            <img
              src="/logo.png"
              alt="MZ Scents"
              className="h-11 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Right actions */}
          <div className="flex items-center gap-5">
            <Link
              to="/shop"
              className="hidden md:flex text-ink hover:text-gold transition-colors"
              aria-label="Search"
              data-testid="search-link"
            >
              <Search size={18} strokeWidth={1.25} />
            </Link>
            <button
              data-testid="open-cart-btn"
              onClick={() => setOpen(true)}
              className="relative text-ink hover:text-gold transition-colors"
              aria-label="Open cart"
            >
              <ShoppingBag size={20} strokeWidth={1.25} />
              {count > 0 && (
                <span
                  data-testid="cart-count-badge"
                  className="absolute -top-2 -right-2 bg-ink text-cream text-[10px] font-medium tracking-wider min-w-[18px] h-[18px] rounded-full flex items-center justify-center px-1"
                >
                  {count}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {mobileOpen && (
          <div
            data-testid="mobile-menu"
            className="md:hidden pb-6 pt-2 border-t border-gold-soft/50 animate-fade-in"
          >
            <nav className="flex flex-col gap-4">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/shop"}
                  className={({ isActive }) =>
                    `text-[11px] uppercase tracking-[0.28em] font-medium py-2 ${
                      isActive ? "text-gold" : "text-ink"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
